import { PauseIcon, PlayIcon } from '@heroicons/react/24/solid';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import Marquee from 'react-fast-marquee';
import { useTimeout } from 'usehooks-ts';
import { ASSET_ROUTE_URL } from '../../../constants/routes';
import { cn } from '../../../lib/classNameHelpers';
import { formatPercentage } from '../../../lib/formatters';
import { Button } from '../../catalyst-ui-kit/button';
import { ColoredMetric } from '../../ColoredMetric/ColoredMetric';
import { ProductWithPrice } from './TradeFavorites';

type ProductDisplayProps = {
  item: ProductWithPrice;
  onSelectProduct: (ticker: string) => void;
  index: number;
};

function ProductDisplay({ item, onSelectProduct, index }: ProductDisplayProps) {
  const { ticker, displayTicker, change24hrPercent } = item;
  const [isHighlighted, setIsHighlighted] = useState(false);
  const previousIndexRef = useRef<number | undefined>(undefined);

  const clearHighlight = useCallback(() => {
    setIsHighlighted(false);
  }, []);

  useTimeout(clearHighlight, isHighlighted ? 1000 : null);

  useEffect(() => {
    const previousIndex = previousIndexRef.current;
    if (previousIndex !== undefined && previousIndex !== index) {
      setIsHighlighted(true);
    }
    previousIndexRef.current = index;
  }, [index]);

  return (
    <Button
      text
      size="2xs"
      role="link"
      color="tertiary"
      onClick={() => {
        onSelectProduct(ticker);
      }}
      className={cn(
        'group flex items-center gap-1 whitespace-nowrap transition-colors duration-1000',
        {
          'text-contentSecondary': isHighlighted,
        }
      )}
    >
      {displayTicker}
      <ColoredMetric className="text-3xs" value={change24hrPercent} formatFn={formatPercentage} />
    </Button>
  );
}

type AutoScrollProps = {
  data: ProductWithPrice[];
  className?: string;
  label: string;
  scrollSpeed?: number;
  fadeSize?: number;
  isPlaying: boolean;
  setIsPlaying: (isPlaying: boolean) => void;
};

export function AutoScroll({
  data,
  className,
  label,
  scrollSpeed = 50,
  fadeSize = 40,
  isPlaying,
  setIsPlaying,
}: AutoScrollProps) {
  const navigate = useNavigate({ from: ASSET_ROUTE_URL });

  const handleSelectProduct = useCallback(
    (ticker: string) => {
      void navigate({
        to: ASSET_ROUTE_URL,
        params: { asset: ticker },
        search: (prev) => prev,
      });
    },
    [navigate]
  );

  const togglePlayPause = useCallback(() => {
    setIsPlaying(!isPlaying);
  }, [isPlaying, setIsPlaying]);

  return (
    <div className={cn('group flex min-w-0 items-center', className)}>
      <p className="text-2xs leading-none text-contentTertiary">{label}</p>

      {/* Auto-scroll Content */}
      <div className="ml-1 flex-1 overflow-hidden">
        <div
          className="scrollbar-hide overflow-hidden"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            maskImage: `linear-gradient(to right, transparent 0%, black ${fadeSize}px, black calc(100% - ${fadeSize}px), transparent 100%)`,
            WebkitMaskImage: `linear-gradient(to right, transparent 0%, black ${fadeSize}px, black calc(100% - ${fadeSize}px), transparent 100%)`,
          }}
        >
          <Marquee speed={scrollSpeed} play={isPlaying} pauseOnHover delay={1} autoFill>
            <div className="flex gap-3 py-1.5 pr-3">
              {data.map((item, index) => (
                <ProductDisplay
                  key={item.displayTicker}
                  item={item}
                  onSelectProduct={handleSelectProduct}
                  index={index}
                />
              ))}
            </div>
          </Marquee>
        </div>
      </div>

      {/* Play/Pause Button */}
      <Button
        text
        size="sm"
        color="secondary"
        onClick={togglePlayPause}
        aria-label={isPlaying ? 'Pause auto-scroll' : 'Play auto-scroll'}
        className="ml-1 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
      >
        {isPlaying ? <PauseIcon className="size-4" /> : <PlayIcon className="size-4" />}
      </Button>
    </div>
  );
}
